import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { StudentWorkTab } from '@/features/Trainer/StudentWorkTab';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { trainerApi, type StudentWork } from '@/lib/api/trainer';
import { useAuthStore } from '@/stores/authStore';

/**
 * One student's whole course record, opened from a queue row.
 *
 * The queue tells a trainer to go and talk to someone; this is what they read
 * on the way. Without it every row is a dead end — a name and an accusation
 * with no way to check it before walking over, which is exactly the situation
 * that makes an integrity signal dangerous.
 *
 * Reuses the work feed rather than inventing a second view of the same data:
 * the trainer should see the same list here as on the student's own page.
 */
export function StudentWorkDrawer({
  batchId,
  student,
  onClose,
}: {
  batchId: string;
  student: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  // From the store rather than a prop: the caller should not get to decide
  // which routes it is allowed to send someone to.
  const isOrgAdmin = useAuthStore((st) => st.user?.role === 'org_admin');
  const { data, isLoading, isError } = useQuery<StudentWork>({
    queryKey: ['studentWork', batchId, student?.id],
    queryFn: async () => (await trainerApi.getStudentWork(batchId, student!.id)).data,
    enabled: !!student,
  });

  return (
    <Sheet open={!!student} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="pr-8 text-base">{student?.name}</SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-2" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          )}
          {isError && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              This student&apos;s work could not be loaded.
            </p>
          )}
          {data && (
            <StudentWorkTab
              work={data}
              // Every row here leads to the whole batch against this work, which
              // self-directed practice cannot have — nobody else was set it.
              // Their full record, including that practice, is one click away on
              // their own page.
              showSelf={false}
              onOpenItem={(item) => {
                // Closed explicitly before navigating: the drawer's open state
                // lives in the queue, which survives the route change.
                onClose();
                // Routed by who is looking. This panel renders for a trainer
                // and for an org admin, and the trainer route is behind a role
                // guard — sending a TPO there bounced them silently back to
                // /org, which read as "the platform will not show me my own
                // students' work".
                void navigate(
                  isOrgAdmin
                    ? {
                        to: '/org/batches/$batchId/work-items/$assessmentId',
                        params: { batchId, assessmentId: item.id },
                      }
                    : {
                        to: '/trainer/batches/$batchId/work-items/$assessmentId',
                        params: { batchId, assessmentId: item.id },
                      },
                );
              }}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
